/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { ModalShell } from '../../../shared/modals/ModalShell'
import { ModalFormField } from '../../../shared/modals/ModalFormField'
import { ModalFormSection } from '../../../shared/modals/ModalFormSection'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { showSuccess } from '../../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { posSharedUi as posUi } from '../../../shared/ui/posSharedUi';
import { createCustomer } from '../../../customers/public-api';

type Props = {
  visible: boolean;
  onClose: () => void;
  onCreated: (customerId: string, customerName: string) => void;
};

export function CheckoutQuickAddCustomerSheet({ visible, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setName('');
      setPhone('');
      setSaving(false);
      setError(null);
    }
  }, [visible]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('El nombre es obligatorio.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const customerId = await createCustomer({
        name: trimmedName,
        phone: phone.trim(),
        creditLimit: 0,
      });
      onCreated(customerId, trimmedName);
      showSuccess('Cliente creado', '');
      onClose();
    } catch (err: unknown) {
      setError('No se pudo crear el cliente.');
      presentUnexpectedFailure({ module: 'pos', op: 'quick_add_customer', error: err });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="Crear cliente"
      closeAccessibilityLabel="Cerrar crear cliente"
      widthVariant="compact"
      scrollable={false}
      keyboardAvoiding
      footer={
        <View style={[posUi.modalActions, modalExperience.footerActions]}>
          <TouchableOpacity style={posUi.secondaryButton} onPress={onClose} disabled={saving}>
            <Text style={posUi.secondaryButtonText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[posUi.primaryButton, saving && posUi.disabledCard]}
            onPress={() => void handleSave()}
            disabled={saving || !name.trim()}
          >
            {saving ? (
              <ActivityIndicator color={ticketColors.goldInk} />
            ) : (
              <Text style={posUi.primaryButtonText}>Crear y seleccionar</Text>
            )}
          </TouchableOpacity>
        </View>
      }
    >
      <ModalFormSection title="Datos del cliente">
        <ModalFormField label="Nombre" required>
          <TextInput
            style={modalExperience.formInput}
            value={name}
            onChangeText={setName}
            placeholder="Nombre del cliente"
            placeholderTextColor={ticketColors.ink3}
            autoCapitalize="words"
            autoCorrect={false}
            editable={!saving}
            accessibilityLabel="Nombre del cliente"
            testID="pos.checkout.quick-add.name"
          />
        </ModalFormField>
        <ModalFormField label="Teléfono">
          <TextInput
            style={modalExperience.formInput}
            value={phone}
            onChangeText={setPhone}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            keyboardType="phone-pad"
            autoCorrect={false}
            editable={!saving}
            accessibilityLabel="Teléfono del cliente"
            testID="pos.checkout.quick-add.phone"
          />
        </ModalFormField>
        {error ? (
          <Text style={{ color: ticketColors.bad, fontSize: 13, fontWeight: '600', fontFamily: ticketFonts.sans }}>
            {error}
          </Text>
        ) : null}
      </ModalFormSection>
    </ModalShell>
  );
}
